import React, { useState, useEffect, useContext } from "react";
import { useMutation } from "@apollo/client";
import { RouteComponentProps, useParams, withRouter } from "react-router-dom";

import { getBUFromLocalStorage } from "helpers/storage";

import { CommonContext } from "config/commonProvider";

import { MOVE_GROUPS_TO_FIELD } from "api/MyLivestock/Groups/mutations";

import { BusinessUnitFieldSelect } from "components/MyLivestock";

import { Modal } from "components";

import { useHasFeature } from "hooks";

interface Props extends RouteComponentProps<{ groupId: string }> {
  showModal: boolean;
  onToggle: () => void;
}

const MoveGroupOfAnimalsToFieldModal = ({ showModal, onToggle }: Props): JSX.Element => {
  const { groupId } = useParams<LivestockGroupPageParams>();

  const [field, setField] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [moveGroupsToField] = useMutation(MOVE_GROUPS_TO_FIELD);
  const { showNotification } = useContext(CommonContext);
  const useServerPaginatedAnimalsList = useHasFeature("SERVER_SIDE_PAGINATION_FOR_ANIMALS_LIST");

  const isConfirmButtonDisabled = field === "" || requesting;

  useEffect(() => {
    if (!showModal) {
      setField("");
    }
  }, [showModal]);

  const handleCancelClick = (e: React.SyntheticEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onToggle();
  };

  const handleConfirmClick = async (): Promise<void> => {
    try {
      setRequesting(true);

      await moveGroupsToField({
        variables: {
          input: {
            businessUnit: +getBUFromLocalStorage(),
            groups: [groupId],
            field,
          },
        },
        refetchQueries: useServerPaginatedAnimalsList ? ["GetAnimalsPage"] : ["GetGroup"],
      });

      onToggle();
      showNotification({
        message: "Group successfully moved",
      });
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Error moving group",
      });
    }

    setRequesting(false);
  };

  return (
    <Modal
      showCloseButton
      active={showModal}
      handleClose={onToggle}
      title="Select location"
      subTitle="Please select a location to move these animals to"
      actions={{
        secondary: {
          onClick: handleCancelClick,
          "data-testid": "cancel-button",
          caption: "Cancel",
        },
        primary: {
          onClick: handleConfirmClick,
          caption: "Move group",
          disabled: isConfirmButtonDisabled,
          requesting,
        },
      }}
    >
      <BusinessUnitFieldSelect value={field} onSelect={setField} />
    </Modal>
  );
};

export default withRouter(MoveGroupOfAnimalsToFieldModal);
